import type {
  BoxStorageRecord,
  CrudPort,
  DataStorage,
  EntityTypeStorageRecord,
  JournalStorageRecord,
  ListQuery,
  ListResult,
  ProductStorageRecord,
  StorageCapabilities,
  StorageHealthStatus,
  StorageSnapshot,
  StorageSortRule,
  UserStorageRecord,
} from "../../domain/storage";
import type {
  BoxId,
  EntityTypeId,
  JournalId,
  ProductId,
  UserId,
} from "../../types/ids";

type RecordWithId<TId extends string> = {
  id: TId;
};

function cloneRecord<TRecord>(record: TRecord): TRecord {
  return { ...record };
}

function normalizeOffset(offset: number | undefined): number {
  if (!Number.isFinite(offset)) {
    return 0;
  }
  return Math.max(0, Math.floor(offset ?? 0));
}

function normalizeLimit(limit: number | undefined): number | null {
  if (limit === undefined || limit === null) {
    return null;
  }
  if (!Number.isFinite(limit)) {
    return null;
  }
  return Math.max(0, Math.floor(limit));
}

function compareUnknown(left: unknown, right: unknown): number {
  if (left === right) {
    return 0;
  }
  if (left === null || left === undefined) {
    return -1;
  }
  if (right === null || right === undefined) {
    return 1;
  }
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  if (typeof left === "boolean" && typeof right === "boolean") {
    return Number(left) - Number(right);
  }
  return String(left).localeCompare(String(right), "ru-RU");
}

function matchesFilter(
  record: Record<string, unknown>,
  filter: Record<string, unknown>,
): boolean {
  return Object.entries(filter).every(([key, expected]) => {
    if (expected === undefined) {
      return true;
    }

    const actual = record[key];
    if (Array.isArray(expected)) {
      if (Array.isArray(actual)) {
        return expected.every((item) => actual.includes(item));
      }
      return expected.includes(actual);
    }

    return actual === expected;
  });
}

function compareBySortRules<TRecord>(
  left: TRecord,
  right: TRecord,
  rules: ReadonlyArray<StorageSortRule>,
): number {
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;

  for (const rule of rules) {
    const direction = rule.direction === "desc" ? -1 : 1;
    const diff = compareUnknown(
      leftRecord[rule.field],
      rightRecord[rule.field],
    );
    if (diff !== 0) {
      return diff * direction;
    }
  }

  return 0;
}

class InMemoryCrudPort<
  TRecord extends RecordWithId<TId>,
  TId extends string,
  TFilter extends Record<string, unknown> = Record<string, unknown>,
> implements CrudPort<TRecord, TId, TFilter> {
  constructor(private readonly store: Map<TId, TRecord>) {}

  async list(query?: ListQuery<TFilter>): Promise<ListResult<TRecord>> {
    const rawItems = Array.from(this.store.values()).map(cloneRecord);
    const filter = query?.filter as Record<string, unknown> | undefined;
    const sortRules = query?.sort ?? [];

    const filtered = !filter
      ? rawItems
      : rawItems.filter((item) =>
          matchesFilter(item as Record<string, unknown>, filter),
        );

    const sorted =
      sortRules.length === 0
        ? filtered
        : [...filtered].sort((left, right) =>
            compareBySortRules(left, right, sortRules),
          );

    const offset = normalizeOffset(query?.offset);
    const limit = normalizeLimit(query?.limit);
    const paged =
      limit === null
        ? sorted.slice(offset)
        : sorted.slice(offset, offset + limit);

    return {
      items: paged.map(cloneRecord),
      total: sorted.length,
      offset,
      limit,
    };
  }

  async getById(id: TId): Promise<TRecord | null> {
    const found = this.store.get(id);
    return found ? cloneRecord(found) : null;
  }

  async upsert(record: TRecord): Promise<TRecord> {
    this.store.set(record.id, cloneRecord(record));
    return cloneRecord(record);
  }

  async remove(id: TId): Promise<void> {
    this.store.delete(id);
  }
}

export class InMemoryDataStorage implements DataStorage {
  readonly mode = "local" as const;

  readonly capabilities: StorageCapabilities = {
    offlineRead: true,
    offlineWrite: true,
    realtimeUpdates: false,
    multiUser: false,
    exportImport: true,
    conflictResolution: "none",
  };

  private readonly productStore = new Map<ProductId, ProductStorageRecord>();
  private readonly boxStore = new Map<BoxId, BoxStorageRecord>();
  private readonly journalStore = new Map<JournalId, JournalStorageRecord>();
  private readonly userStore = new Map<UserId, UserStorageRecord>();
  private readonly entityTypeStore = new Map<
    EntityTypeId,
    EntityTypeStorageRecord
  >();

  readonly products = new InMemoryCrudPort(this.productStore);
  readonly boxes = new InMemoryCrudPort(this.boxStore);
  readonly journals = new InMemoryCrudPort(this.journalStore);
  readonly users = new InMemoryCrudPort(this.userStore);
  readonly entityTypes = new InMemoryCrudPort(this.entityTypeStore);

  constructor(initialSnapshot?: StorageSnapshot | null) {
    if (initialSnapshot) {
      this.loadSnapshot(initialSnapshot);
    }
  }

  async healthCheck(): Promise<StorageHealthStatus> {
    return {
      isAvailable: true,
      lastCheckedAt: new Date().toISOString(),
      message: null,
    };
  }

  async runInTransaction<T>(operation: () => Promise<T>): Promise<T> {
    return operation();
  }

  async exportSnapshot(): Promise<StorageSnapshot> {
    return {
      schemaVersion: 1,
      exportedAt: new Date().toISOString(),
      data: {
        products: Array.from(this.productStore.values()).map(cloneRecord),
        boxes: Array.from(this.boxStore.values()).map(cloneRecord),
        journals: Array.from(this.journalStore.values()).map(cloneRecord),
        users: Array.from(this.userStore.values()).map(cloneRecord),
        entityTypes: Array.from(this.entityTypeStore.values()).map(cloneRecord),
      },
    };
  }

  async importSnapshot(snapshot: StorageSnapshot): Promise<void> {
    this.loadSnapshot(snapshot);
  }

  private loadSnapshot(snapshot: StorageSnapshot): void {
    this.productStore.clear();
    this.boxStore.clear();
    this.journalStore.clear();
    this.userStore.clear();
    this.entityTypeStore.clear();

    snapshot.data.products.forEach((item) => {
      this.productStore.set(item.id, cloneRecord(item));
    });
    snapshot.data.boxes.forEach((item) => {
      this.boxStore.set(item.id, cloneRecord(item));
    });
    snapshot.data.journals.forEach((item) => {
      this.journalStore.set(item.id, cloneRecord(item));
    });
    snapshot.data.users.forEach((item) => {
      this.userStore.set(item.id, cloneRecord(item));
    });
    snapshot.data.entityTypes.forEach((item) => {
      this.entityTypeStore.set(item.id, cloneRecord(item));
    });
  }
}
